<template>
    <div>
        <UiCardListHeader
            :action-href="blockId ? route('content/blocks.admin.edit', { id: blockId }) : ''"
            :action-label="lang('content::content.widgets.block_editor.edit_full')"
        >
            <template #title>
                <span v-if="block">{{ block.name }}</span>
                <span v-else>{{ lang('content::content.widgets.block_editor.widget_name') }}</span>
            </template>
        </UiCardListHeader>

        <div v-if="loading" class="py-6 text-sm text-center text-neutral-500">
            {{ lang('general.loading') }}
        </div>

        <div
            v-else-if="warning"
            class="px-4 py-2 text-yellow-800 bg-yellow-100 border border-yellow-300 dark:bg-yellow-900/20 dark:text-yellow-200 dark:border-yellow-800 rounded text-sm"
        >
            <div class="flex items-center space-x-2">
                <icon icon="hero/outline/exclamation-triangle" class="w-5 h-5"></icon>
                <span>{{ warning }}</span>
            </div>
        </div>

        <UiCard v-else-if="contentItem" class="p-4">
            <div class="space-y-4">
                <div v-for="attribute in filteredAttributes" :key="attribute.slug">
                    <Field v-model="formData[attribute.slug]" :field="attribute" label-position="top" />
                </div>
            </div>

            <div class="flex justify-end pt-4 mt-4 border-t border-neutral-200 dark:border-neutral-700">
                <Button color="primary" :loading="saving" :disabled="saving" @click="save">
                    {{ lang('general.save') }}
                </Button>
            </div>
        </UiCard>

        <UiCardEmpty v-else>
            {{ lang('content::content.widgets.block_editor.no_selection') }}
        </UiCardEmpty>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { lang } from '@/lang/composable';
import { useToast } from '@src/Common/Composables/useToast';
import { filterAttributeSegment } from '@src/Content/Pages/Builder/helpers';
import Button from '@src/Common/Components/Button.vue';

interface ContentItemData {
    id: number;
    type: string;
    value: Record<string, unknown>;
    attributes?: Array<{
        slug: string;
        segment?: string;
        [key: string]: unknown;
    }>;
}

interface BlockData {
    id: number;
    name: string;
    content: ContentItemData[];
}

interface Props {
    settings: {
        block_content?: {
            block_id?: number;
            content_item_index?: number;
        };
    };
}

const props = defineProps<Props>();

const blockId = computed(() => props.settings?.block_content?.block_id ?? null);
const contentItemIndex = computed(() => props.settings?.block_content?.content_item_index ?? null);

const loading = ref(true);
const block = ref<BlockData | null>(null);
const contentItem = ref<ContentItemData | null>(null);
const contentItemType = ref<string | null>(null);
const attributes = ref<Array<{ slug: string; segment?: string; [key: string]: unknown }>>([]);
const warning = ref<string | null>(null);

const formData = ref<Record<string, unknown>>({});
const saving = ref(false);

const filteredAttributes = computed(() => {
    return filterAttributeSegment(attributes.value, null);
});

const loadData = async () => {
    if (!blockId.value || contentItemIndex.value === null) {
        loading.value = false;
        return;
    }

    loading.value = true;
    warning.value = null;

    try {
        const { data } = await axios.get(route('content.builder.blocks'));
        const foundBlock = data.blocks?.find((b: BlockData) => b.id === blockId.value);

        if (!foundBlock) {
            warning.value = lang('content::content.widgets.block_editor.block_not_found');
            loading.value = false;
            return;
        }

        block.value = foundBlock;

        const content = foundBlock.content ?? [];
        const index = contentItemIndex.value;

        if (index < 0 || index >= content.length) {
            warning.value = lang('content::content.widgets.block_editor.item_not_found');
            loading.value = false;
            return;
        }

        const item = content[index];
        contentItem.value = item;
        contentItemType.value = item.type ?? null;

        // Load type attributes from API
        if (item.type) {
            const { data: typesData } = await axios.get(route('content.builder.types'));
            const typeInfo = typesData.types?.find((t: { slug: string }) => t.slug === item.type);
            attributes.value = typeInfo?.attributes ?? [];
        }

        // Initialize form data
        if (item.value) {
            formData.value = { ...item.value };
        }
    } catch (error) {
        console.error('Error loading widget data:', error);
        warning.value = lang('content::content.widgets.block_editor.save_error');
    } finally {
        loading.value = false;
    }
};

const save = async () => {
    if (!blockId.value || contentItemIndex.value === null || !block.value) {
        return;
    }

    saving.value = true;

    try {
        const updatedContent = block.value.content.map((item, index) => {
            if (index === contentItemIndex.value) {
                return {
                    ...item,
                    value: { ...formData.value },
                };
            }
            return { ...item };
        });

        const { data } = await axios.post(route('content/blocks.admin.api.update', { id: blockId.value }), {
            _method: 'PUT',
            title: block.value.name,
            content: updatedContent,
        });

        block.value.content = updatedContent;
        contentItem.value = updatedContent[contentItemIndex.value];

        useToast({ message: data.message ?? lang('content::content.widgets.block_editor.saved') });
    } catch (error) {
        console.error('Error saving content item:', error);
        useToast({ type: 'error', message: lang('content::content.widgets.block_editor.save_error') });
    } finally {
        saving.value = false;
    }
};

onMounted(() => {
    loadData();
});
</script>
